// ---------------------------------------------------------------------------
// DnD Email Editor – Data Model & Immutable CRUD Helpers
// ---------------------------------------------------------------------------

export const CURRENT_EDITOR_VERSION = 1;

// ---- Primitives -----------------------------------------------------------

export interface Spacing {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface BorderSide {
  width: number;
  style: 'none' | 'solid' | 'dashed' | 'dotted';
  color: string;
}

export interface BorderSides {
  top: BorderSide;
  right: BorderSide;
  bottom: BorderSide;
  left: BorderSide;
  /**
   * Corner rounding for the bordered box, in px. Absent/0 = square. Renders
   * everywhere except Outlook desktop, which ignores border-radius and falls
   * back to square corners (standard graceful degradation for email).
   */
  radius?: number;
}

/** True when any side has spacing — a zero Spacing compiles to nothing. */
export function hasSpacing(s: Spacing | undefined): s is Spacing {
  return !!s && (s.top > 0 || s.right > 0 || s.bottom > 0 || s.left > 0);
}

/** True when any side would actually draw (visible style and a real width). */
export function hasVisibleBorder(b: BorderSides | undefined): b is BorderSides {
  if (!b) return false;
  return ([b.top, b.right, b.bottom, b.left] as BorderSide[]).some(
    (side) => side.style !== 'none' && side.width > 0,
  );
}

// ---- Global Settings ------------------------------------------------------

export interface HeadingStyle {
  fontSize: string;
  color: string;
  fontWeight: string;
}

export interface DefaultButtonStyle {
  bgColor: string;
  textColor: string;
  borderRadius: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
}

export interface GlobalStyles {
  bodyBgColor: string;
  contentBgColor: string;
  fontFamily: string;
  fontSize: string;
  textColor: string;
  lineHeight: string;
  linkColor: string;
  linkUnderline: boolean;
  headingFontFamily: string;
  h1: HeadingStyle;
  h2: HeadingStyle;
  h3: HeadingStyle;
  defaultButtonStyle: DefaultButtonStyle;
}

export interface WrapperSettings {
  maxWidth: number;
  padding: Spacing;
  bgColor: string;
}

// ---- Layer Base -----------------------------------------------------------

export interface LayerBase {
  id: string;
  label: string;
  locked: boolean;
  bgColor: string;
  textColor: string;
  bgImage: string;
  bgRepeat: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';
  bgPosition: string;
  bgSize: 'cover' | 'contain' | 'auto';
  contentBgColor: string;
  padding: Spacing;
  border: BorderSides;
  /**
   * Outer spacing: transparent space between the block's border/background and
   * its neighbours / the content edge. Compiled as padding on a wrapping table
   * cell (never CSS margin — Outlook's Word engine ignores margin).
   */
  margin: Spacing;
  hideOnMobile: boolean;
  hideOnDesktop: boolean;
}

// ---- Concrete Layer Types -------------------------------------------------

export interface TextLayer extends LayerBase {
  type: 'text';
  content: string;
  textAlign: 'left' | 'center' | 'right';
  /** Per-block line height (e.g. "24px"). Empty/undefined inherits the global line height. */
  lineHeight?: string;
}

export interface ImageLayer extends LayerBase {
  type: 'image';
  src: string;
  alt: string;
  width: number;
  autoWidth: boolean;
  href: string;
  target: '_blank' | '_self';
  align: 'left' | 'center' | 'right';
  borderRadius: number;
  retina: boolean;
}

export interface ButtonLayer extends LayerBase {
  type: 'button';
  text: string;
  href: string;
  target: '_blank' | '_self';
  buttonBgColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  borderRadius: number;
  buttonPadding: Spacing;
  fullWidth: boolean;
  align: 'left' | 'center' | 'right';
}

export interface DividerLayer extends LayerBase {
  type: 'divider';
  borderColor: string;
  borderStyle: 'solid' | 'dashed' | 'dotted';
  borderWidth: number;
  dividerWidth: string;
  align: 'left' | 'center' | 'right';
}

export interface SpacerLayer extends LayerBase {
  type: 'spacer';
  height: number;
}

export interface HtmlLayer extends LayerBase {
  type: 'html';
  content: string;
}

export type ColumnsLayout = '1' | '2' | '3' | '4' | '1:2' | '2:1' | '1:3' | '3:1';

export interface ColumnsLayer extends LayerBase {
  type: 'columns';
  layout: ColumnsLayout;
  columnsGap: number;
  stackOnMobile: boolean;
  /** Reverse the stacking order of columns on mobile. */
  reverseStackOnMobile?: boolean;
  /**
   * Optional per-column width percentages overriding the layout preset.
   * When absent (the default), widths come from the layout preset.
   * Seam for future custom-width UI; not surfaced yet.
   */
  columnWidths?: number[];
  columns: Column[];
}

export type SocialPlatform =
  | 'facebook'
  | 'instagram'
  | 'x'
  | 'linkedin'
  | 'youtube'
  | 'tiktok'
  | 'pinterest'
  | 'website'
  | 'email';

export interface SocialLink {
  platform: SocialPlatform;
  url: string;
  enabled: boolean;
}

/**
 * Per-platform hosted PNG icon URLs, snapshotted from the selected social logo
 * set. Present only when a set is chosen; empty/absent means render the
 * built-in monochrome glyphs. Snapshotted (rather than resolved from the set id
 * at compile time) so the compiler/preview stay pure and the saved email HTML
 * is stable if the set is later edited.
 */
export type SocialLogos = Partial<Record<SocialPlatform, string>>;

export interface SocialLayer extends LayerBase {
  type: 'social';
  style: 'icons-only' | 'icons-text' | 'text-only';
  iconSize: number;
  align: 'left' | 'center' | 'right';
  spacing: number;
  /**
   * Line height for the row of icons (e.g. "48px"). Adds vertical space between
   * rows when the icons wrap. Empty/undefined leaves the row unspaced.
   */
  lineHeight?: string;
  /** Icon/label color. Empty inherits the surrounding text color. */
  iconColor?: string;
  links: SocialLink[];
  /** Selected social logo set id. Null/undefined = built-in monochrome icons. */
  logoSetId?: number | null;
  /** Resolved PNG URLs from the selected set (see SocialLogos). */
  logos?: SocialLogos;
}

export interface VideoLayer extends LayerBase {
  type: 'video';
  thumbnailSrc: string;
  videoUrl: string;
  playButtonColor: string;
  alt: string;
}

export interface CountdownLayer extends LayerBase {
  type: 'countdown';
  targetDate: string;
  timezone: string;
  countdownStyle: 'dark' | 'light' | 'custom';
  labelColor: string;
  digitColor: string;
  digitBgColor: string;
  showLabels: boolean;
  labels: { days: string; hours: string; minutes: string; seconds: string };
}

export type Layer =
  | TextLayer
  | ImageLayer
  | ButtonLayer
  | DividerLayer
  | SpacerLayer
  | HtmlLayer
  | ColumnsLayer
  | SocialLayer
  | VideoLayer
  | CountdownLayer;

export type LayerType = Layer['type'];

// ---- Elements (inside columns) --------------------------------------------

export interface ElementBase {
  id: string;
  padding: Spacing;
  /**
   * Border around the element. When visible, the compiler moves `padding` onto
   * the bordered table cell (Outlook ignores padding on a <div>), so padding is
   * the space between the border and the content.
   */
  border: BorderSides;
  /** Outer spacing around the element's border — see LayerBase.margin. */
  margin: Spacing;
  hideOnMobile: boolean;
  hideOnDesktop: boolean;
  /**
   * When false, text/image elements are editable by downstream (HTML editor)
   * users — compiled with data-ftp-unlocked. Defaults to locked. Only
   * meaningful for text and image elements.
   */
  locked?: boolean;
}

export interface TextElement extends ElementBase {
  type: 'text';
  content: string;
  textAlign: 'left' | 'center' | 'right';
  /** Per-block line height (e.g. "24px"). Empty/undefined inherits the global line height. */
  lineHeight?: string;
}

export interface ImageElement extends ElementBase {
  type: 'image';
  src: string;
  alt: string;
  width: number;
  autoWidth: boolean;
  href: string;
  target: '_blank' | '_self';
  align: 'left' | 'center' | 'right';
  borderRadius: number;
  retina: boolean;
}

export interface ButtonElement extends ElementBase {
  type: 'button';
  text: string;
  href: string;
  target: '_blank' | '_self';
  bgColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  borderRadius: number;
  buttonPadding: Spacing;
  fullWidth: boolean;
  align: 'left' | 'center' | 'right';
}

export interface DividerElement extends ElementBase {
  type: 'divider';
  borderColor: string;
  borderStyle: 'solid' | 'dashed' | 'dotted';
  borderWidth: number;
  dividerWidth: string;
}

export interface SpacerElement extends ElementBase {
  type: 'spacer';
  height: number;
}

export interface HtmlElement extends ElementBase {
  type: 'html';
  content: string;
}

export interface SocialElement extends ElementBase {
  type: 'social';
  style: 'icons-only' | 'icons-text' | 'text-only';
  iconSize: number;
  align: 'left' | 'center' | 'right';
  spacing: number;
  /**
   * Line height for the row of icons (e.g. "48px"). Adds vertical space between
   * rows when the icons wrap. Empty/undefined leaves the row unspaced.
   */
  lineHeight?: string;
  /** Icon/label color. Empty inherits the surrounding text color. */
  iconColor?: string;
  links: SocialLink[];
  /** Selected social logo set id. Null/undefined = built-in monochrome icons. */
  logoSetId?: number | null;
  /** Resolved PNG URLs from the selected set (see SocialLogos). */
  logos?: SocialLogos;
}

export type Element =
  | TextElement
  | ImageElement
  | ButtonElement
  | DividerElement
  | SpacerElement
  | HtmlElement
  | SocialElement;

export type ElementType = Element['type'];

// ---- Column ---------------------------------------------------------------

export interface Column {
  id: string;
  bgColor: string;
  padding: Spacing;
  border: BorderSides;
  valign: 'top' | 'middle' | 'bottom';
  elements: Element[];
}

// ---- Root Model -----------------------------------------------------------

export interface DndEmailModel {
  editorVersion: number;
  globalStyles: GlobalStyles;
  wrapper: WrapperSettings;
  preheaderText: string;
  layers: Layer[];
}

// ---------------------------------------------------------------------------
// ID generation
// ---------------------------------------------------------------------------

let _idCounter = 0;

export function generateId(prefix = 'dnd'): string {
  _idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${_idCounter.toString(36)}`;
}

// ---------------------------------------------------------------------------
// Immutable CRUD helpers – all return a new model
// ---------------------------------------------------------------------------

export function addLayer(
  model: DndEmailModel,
  layer: Layer,
  index?: number,
): DndEmailModel {
  const layers = [...model.layers];
  if (index !== undefined && index >= 0 && index <= layers.length) {
    layers.splice(index, 0, layer);
  } else {
    layers.push(layer);
  }
  return { ...model, layers };
}

export function addLayers(
  model: DndEmailModel,
  newLayers: Layer[],
  index?: number,
): DndEmailModel {
  if (newLayers.length === 0) return model;
  const layers = [...model.layers];
  if (index !== undefined && index >= 0 && index <= layers.length) {
    layers.splice(index, 0, ...newLayers);
  } else {
    layers.push(...newLayers);
  }
  return { ...model, layers };
}

export function removeLayer(
  model: DndEmailModel,
  layerId: string,
): DndEmailModel {
  return { ...model, layers: model.layers.filter((l) => l.id !== layerId) };
}

export function moveLayer(
  model: DndEmailModel,
  fromIndex: number,
  toIndex: number,
): DndEmailModel {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= model.layers.length ||
    toIndex >= model.layers.length
  )
    return model;
  const layers = [...model.layers];
  const [moved] = layers.splice(fromIndex, 1);
  layers.splice(toIndex, 0, moved);
  return { ...model, layers };
}

function deepCloneWithNewIds<T extends { id: string }>(item: T): T {
  const json = JSON.parse(JSON.stringify(item)) as T;
  json.id = generateId();
  if ('columns' in json && Array.isArray((json as Record<string, unknown>).columns)) {
    const cols = (json as unknown as ColumnsLayer).columns;
    for (const col of cols) {
      col.id = generateId('col');
      for (let i = 0; i < col.elements.length; i++) {
        col.elements[i] = { ...col.elements[i], id: generateId('el') };
      }
    }
  }
  return json;
}

/**
 * Deep-clone a set of layers, minting fresh ids for each layer and any nested
 * columns/elements. Used when inserting a reusable/saved block so the same
 * source layers can be dropped repeatedly without id collisions.
 */
export function cloneLayersWithNewIds(layers: Layer[]): Layer[] {
  return layers.map((l) => deepCloneWithNewIds(l));
}

export function duplicateLayer(
  model: DndEmailModel,
  layerId: string,
): DndEmailModel {
  const idx = model.layers.findIndex((l) => l.id === layerId);
  if (idx === -1) return model;
  const clone = deepCloneWithNewIds(model.layers[idx]);
  clone.label = `${clone.label} (copy)`;
  const layers = [...model.layers];
  layers.splice(idx + 1, 0, clone);
  return { ...model, layers };
}

export function updateLayer(
  model: DndEmailModel,
  layerId: string,
  patch: Partial<Layer>,
): DndEmailModel {
  return {
    ...model,
    layers: model.layers.map((l) =>
      l.id === layerId ? ({ ...l, ...patch } as Layer) : l,
    ),
  };
}

// ---- Element CRUD (inside a column within a columns layer) ----------------

function mapColumns(
  model: DndEmailModel,
  layerId: string,
  fn: (columns: Column[]) => Column[],
): DndEmailModel {
  return {
    ...model,
    layers: model.layers.map((l) => {
      if (l.id !== layerId || l.type !== 'columns') return l;
      return { ...l, columns: fn([...(l as ColumnsLayer).columns]) };
    }),
  };
}

export function addElement(
  model: DndEmailModel,
  layerId: string,
  columnId: string,
  element: Element,
  index?: number,
): DndEmailModel {
  return mapColumns(model, layerId, (cols) =>
    cols.map((c) => {
      if (c.id !== columnId) return c;
      const elements = [...c.elements];
      if (index !== undefined && index >= 0 && index <= elements.length) {
        elements.splice(index, 0, element);
      } else {
        elements.push(element);
      }
      return { ...c, elements };
    }),
  );
}

export function removeElement(
  model: DndEmailModel,
  layerId: string,
  columnId: string,
  elementId: string,
): DndEmailModel {
  return mapColumns(model, layerId, (cols) =>
    cols.map((c) => {
      if (c.id !== columnId) return c;
      return { ...c, elements: c.elements.filter((e) => e.id !== elementId) };
    }),
  );
}

export function moveElement(
  model: DndEmailModel,
  layerId: string,
  fromColumnId: string,
  fromIndex: number,
  toColumnId: string,
  toIndex: number,
): DndEmailModel {
  return mapColumns(model, layerId, (cols) => {
    let moved: Element | undefined;
    const result = cols.map((c) => {
      if (c.id === fromColumnId) {
        const elems = [...c.elements];
        [moved] = elems.splice(fromIndex, 1);
        return { ...c, elements: elems };
      }
      return c;
    });
    if (!moved) return cols;
    return result.map((c) => {
      if (c.id === toColumnId) {
        const elems = [...c.elements];
        elems.splice(toIndex, 0, moved!);
        return { ...c, elements: elems };
      }
      return c;
    });
  });
}

export function updateElement(
  model: DndEmailModel,
  layerId: string,
  columnId: string,
  elementId: string,
  patch: Partial<Element>,
): DndEmailModel {
  return mapColumns(model, layerId, (cols) =>
    cols.map((c) => {
      if (c.id !== columnId) return c;
      return {
        ...c,
        elements: c.elements.map((e) =>
          e.id === elementId ? ({ ...e, ...patch } as Element) : e,
        ),
      };
    }),
  );
}

export function updateColumn(
  model: DndEmailModel,
  layerId: string,
  columnId: string,
  patch: Partial<Column>,
): DndEmailModel {
  return mapColumns(model, layerId, (cols) =>
    cols.map((c) => (c.id === columnId ? { ...c, ...patch } : c)),
  );
}

export function duplicateElement(
  model: DndEmailModel,
  layerId: string,
  columnId: string,
  elementId: string,
): DndEmailModel {
  return mapColumns(model, layerId, (cols) =>
    cols.map((c) => {
      if (c.id !== columnId) return c;
      const idx = c.elements.findIndex((e) => e.id === elementId);
      if (idx === -1) return c;
      const clone = { ...JSON.parse(JSON.stringify(c.elements[idx])), id: generateId('el') } as Element;
      const elements = [...c.elements];
      elements.splice(idx + 1, 0, clone);
      return { ...c, elements };
    }),
  );
}

// ---- Global / Wrapper updates ---------------------------------------------

export function updateGlobalStyles(
  model: DndEmailModel,
  patch: Partial<GlobalStyles>,
): DndEmailModel {
  return { ...model, globalStyles: { ...model.globalStyles, ...patch } };
}

export function updateWrapper(
  model: DndEmailModel,
  patch: Partial<WrapperSettings>,
): DndEmailModel {
  return { ...model, wrapper: { ...model.wrapper, ...patch } };
}

// ---- Lookup helpers -------------------------------------------------------

export function findLayerById(
  model: DndEmailModel,
  layerId: string,
): Layer | undefined {
  return model.layers.find((l) => l.id === layerId);
}

export function findElementById(
  model: DndEmailModel,
  elementId: string,
): { layer: ColumnsLayer; column: Column; element: Element } | undefined {
  for (const layer of model.layers) {
    if (layer.type !== 'columns') continue;
    for (const col of layer.columns) {
      const el = col.elements.find((e) => e.id === elementId);
      if (el) return { layer, column: col, element: el };
    }
  }
  return undefined;
}

export type DndAlign = 'left' | 'center' | 'right';
